import { Asset, UpdateAssetDocument } from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useGraphQLClient } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { updateAsset } from "../state/slices/assetsSlice"
import { useAppDispatch } from "@hydrologiq/shared/state/hooks"
import { useCallback } from "react"

/**
 * Encapsulates renaming an asset: runs the UpdateAsset mutation, writes the new
 * name into the Apollo cache and keeps the Redux assets slice in sync. Shared by
 * the editable page title and the Information tab so both stay consistent.
 */
export function useRenameAsset(asset?: Asset | null) {
  const [mutateAsset, { loading }] = useMutationWithEndpoint(UpdateAssetDocument, {}, "primary")
  const client = useGraphQLClient("primary")
  const dispatch = useAppDispatch()

  const renameAsset = useCallback(
    async (newName: string) => {
      const name = newName.trim()
      if (!asset || !name || name === asset.name) {
        return
      }

      try {
        const result = await mutateAsset({
          variables: {
            id: asset.id,
            name,
            volume: asset.volume ?? undefined,
            min_pressure: asset.min_pressure ?? undefined,
          },
        })

        if (result.errors?.length) {
          throw result.errors[0]
        }

        // Only update cache and local state after a confirmed success —
        // the `update` callback on the mutation runs even when the response
        // contains GraphQL errors, which would write the invalid name.
        client.cache.modify({
          id: client.cache.identify({ __typename: "Asset", id: asset.id }),
          fields: { name: () => name },
        })
        dispatch(updateAsset({ ...asset, name }))
      } catch (error) {
        console.error("Failed to rename asset:", error)
        throw error
      }
    },
    [asset, mutateAsset, client, dispatch],
  )

  return { renameAsset, renaming: loading }
}
